import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Table } from '@amzn/awsui-components-react';

import {
    getInstructorRoleTypesList,
    selectIsLoading,
    selectIsLoaded,
    selectAllInstructorRoleTypes,
    selectError,
    updateInstructorRoleType,
    selectSelectedInstructorRoleType,
    setSelectedInstructorRoleType,
    addInstructorRoleType,
    selectCount,
    resetInstructorRoleTypesSlice,
} from '../../../common/store/slices/instructorRoleTypesSlice';
import {
    AdminBusinessDataFormInputType,
    AdminBusinessDataFormSchema,
} from '../../interfaces/adminBusinessDataFormSchema';
import { AdminBusinessDataSelectors } from '../../interfaces/adminBusinessDataSelectors';
import AdminBusinessData from '../AdminBusinessData';
import { formatStatus } from '../AdminBusinessData/AdminBusinessData.Status';
import { useNotifications } from '../../../common/context/grimsbyNotifications';
import handleBusinessDataNotification from '../../../common/utils/handleBusinessDataNotification';
import parseBoolean from '../../../common/utils/parseBoolean';
import { InstructorRoleTypeItemData } from '../../../common/interfaces/businessDataItem/instructorRoleTypeItem';

const INSTRUCTOR_ROLE_TYPES_TABLE_TITLE = 'Instructor role types';
const INSTRUCTOR_ROLE_TYPES_DISPLAY_SINGULAR = 'Instructor role type';
export const INSTRUCTOR_ROLE_TYPE_NAME_KEY = 'instructor_role_type';

// this array must exclude the "name" column because
// we need to define it inside AdminBusinessData so we can attach an eventHandler to that link
export const columnDefinitions: Array<Table.ColumnDefinition> = [
    {
        id: 'active',
        header: 'Status',
        cell: (instructorRoleType: InstructorRoleTypeItemData) =>
            formatStatus(instructorRoleType.active.toString()),
    },
];

/**
 * Editable form fields for InstructorRoleType
 * The keys must map to InstructorRoleTypeItemData properties
 */
export const createFormSchema: AdminBusinessDataFormSchema<InstructorRoleTypeItemData> = {
    // the keys must match InstructorRoleTypeItem properties
    instructor_role_type: {
        type: AdminBusinessDataFormInputType.Text,
        label: `${INSTRUCTOR_ROLE_TYPES_DISPLAY_SINGULAR} name`,
        required: true,
        defaultValue: '',
        disabled: false,
        formDataTransform: (
            val: string,
        ): Partial<InstructorRoleTypeItemData> => {
            return { instructor_role_type: val };
        },
    },
    active: {
        type: AdminBusinessDataFormInputType.StatusRadio,
        label: 'Status',
        required: true,
        defaultValue: true,
        disabled: false,
        formDataTransform: (
            val: 'true' | 'false',
        ): Partial<InstructorRoleTypeItemData> => {
            return { active: parseBoolean(val) };
        },
    },
};

// The update form schema is the same as the create form schema except the "name" field is disabled
export const updateFormSchema: AdminBusinessDataFormSchema<InstructorRoleTypeItemData> = {
    ...createFormSchema,
    instructor_role_type: {
        ...createFormSchema.instructor_role_type,
        disabled: true,
    },
};

const AdminInstructorRoleTypes = () => {
    const dispatch = useDispatch();
    const { addNotification } = useNotifications();

    function useInstructorRoleTypes(): AdminBusinessDataSelectors<
        InstructorRoleTypeItemData
    > {
        const dispatch = useDispatch();

        const items: InstructorRoleTypeItemData[] = useSelector(
            selectAllInstructorRoleTypes,
        );
        const itemCount: number = useSelector(selectCount);
        const isLoading: boolean = useSelector(selectIsLoading);
        const isLoaded: boolean = useSelector(selectIsLoaded);
        const error = useSelector(selectError);
        const currentItem: InstructorRoleTypeItemData | null = useSelector(
            selectSelectedInstructorRoleType,
        );
        useEffect(() => {
            dispatch(getInstructorRoleTypesList());
            dispatch(setSelectedInstructorRoleType(null));

            return () => {
                dispatch(resetInstructorRoleTypesSlice());
            };
        }, [dispatch]);
        return { items, itemCount, isLoading, isLoaded, error, currentItem };
    }

    const handleItemSelect = async (
        instructorRoleType: InstructorRoleTypeItemData,
    ) => {
        if (instructorRoleType) {
            dispatch(
                setSelectedInstructorRoleType(
                    instructorRoleType.instructor_role_type,
                ),
            );
            await dispatch(getInstructorRoleTypesList());
        }
    };

    const handleItemUpdate = async (data: InstructorRoleTypeItemData) => {
        dispatch(setSelectedInstructorRoleType(null));
        const dispatchPromise = dispatch<any>(updateInstructorRoleType(data));
        await handleBusinessDataNotification({
            businessDataType: INSTRUCTOR_ROLE_TYPE_NAME_KEY,
            businessDataDisplayNameLower: INSTRUCTOR_ROLE_TYPES_DISPLAY_SINGULAR.toLowerCase(),
            itemName: data.instructor_role_type,
            dispatchPromise: dispatchPromise,
            addNotification: addNotification,
            action: 'update',
        });
    };

    const handleItemCreate = async ({
        instructor_role_type,
        active,
    }: InstructorRoleTypeItemData) => {
        const dispatchPromise = dispatch<any>(
            addInstructorRoleType(active, instructor_role_type),
        );
        await handleBusinessDataNotification({
            businessDataType: INSTRUCTOR_ROLE_TYPE_NAME_KEY,
            businessDataDisplayNameLower: INSTRUCTOR_ROLE_TYPES_DISPLAY_SINGULAR.toLowerCase(),
            itemName: instructor_role_type,
            dispatchPromise: dispatchPromise,
            addNotification: addNotification,
            action: 'create',
        });
    };

    return (
        <div data-testid="AdminBusinessDataManagementInstructorRoleTypes">
            <AdminBusinessData
                columnDefinitions={columnDefinitions}
                createFormSchema={createFormSchema}
                updateFormSchema={updateFormSchema}
                handleItemCreate={handleItemCreate}
                handleItemSelect={handleItemSelect}
                handleItemUpdate={handleItemUpdate}
                itemDisplayNameSingular={INSTRUCTOR_ROLE_TYPES_DISPLAY_SINGULAR}
                itemNameKey={INSTRUCTOR_ROLE_TYPE_NAME_KEY}
                title={INSTRUCTOR_ROLE_TYPES_TABLE_TITLE}
                useItemList={useInstructorRoleTypes}
            />
        </div>
    );
};

export default AdminInstructorRoleTypes;
